import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { products } from '../data/products';

const WHATSAPP_NUMBER = "5531999790497";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Rolar para o topo assim que a página abrir
    window.scrollTo(0, 0);
    
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const foundProduct = products.find(p => p.id === productId);
        if (foundProduct) {
          setProduct(foundProduct);
        } else {
          setError('Produto não encontrado');
        }
      } catch (err) {
        console.error('Erro ao carregar produto:', err);
        setError('Produto não encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8DCC8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-[#8B4513] border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-[#6B5344]">Carregando produto...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#E8DCC8] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#8B4513] mb-4">Produto não encontrado</h2>
          <Link 
            to="/"
            className="inline-block bg-[#8B4513] text-[#F5F0E6] px-6 py-3 rounded hover:bg-[#6B3410] transition-colors"
          >
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  const whatsappMessage = `Olá! Gostaria de encomendar a ${product.name} que vi no site. Poderiam me passar os detalhes?`;
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="min-h-screen bg-[#E8DCC8]" data-testid="product-detail-page">
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Back link */}
        <Link 
          to="/"
          className="inline-flex items-center text-[#8B4513] hover:text-[#DAA520] mb-8 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Voltar para cestas
        </Link>

        {/* Product Details */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Image */}
          <div>
            <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F0E6] border-4 border-[#DAA520] shadow-2xl">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Info */}
          <div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-[#8B4513] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="product-title"
            >
              {product.name}
            </h1>

            <p className="text-[#4A3728] text-lg mb-6" data-testid="product-description">
              {product.description}
            </p>

            {/* Price */}
            <div className="bg-[#F5F0E6] rounded-lg p-6 mb-6 border border-[#C4B8A4]">
              <p className="text-3xl font-bold text-[#8B4513]" data-testid="product-price">
                {product.price_display}
              </p>
              <p className="text-[#6B5344]">+ taxa de entrega</p>
              {product.optional && (
                <p className="text-[#DAA520] mt-2 font-medium text-sm">
                  {product.optional}
                </p>
              )}
            </div>

            {/* Tabela de Preços Específica para Cesta de Frutas e Flores */}
            {product.id === 'cesta-frutas-e-flores' && (
              <div className="bg-[#F5F0E6] rounded-lg p-6 mb-6 border border-[#DAA520]">
                <h4 className="font-bold text-[#8B4513] mb-3 text-lg">Opções de Tamanho:</h4>
                <ul className="space-y-3 text-[#4A3728] font-medium">
                  <li className="flex justify-between border-b border-[#C4B8A4] pb-2">
                    <span>Cesta "P"</span>
                    <span className="font-bold text-[#8B4513]">R$ 368,00</span>
                  </li>
                  <li className="flex justify-between border-b border-[#C4B8A4] pb-2">
                    <span>Cesta "M"</span>
                    <span className="font-bold text-[#8B4513]">R$ 494,00</span>
                  </li>
                  <li className="flex justify-between pb-1">
                    <span>Cesta "G" c/ flores</span>
                    <span className="font-bold text-[#8B4513]">R$ 710,00</span>
                  </li>
                </ul>
              </div>
            )}

            {/* CTA Button */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full bg-[#25D366] text-white py-4 rounded-lg text-lg font-semibold hover:bg-[#20BD5C] transition-colors mb-8"
              data-testid="whatsapp-order-button"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Pedir via WhatsApp
            </a>

            {/* Items List */}
            <div>
              <h3 
                className="text-xl font-semibold text-[#8B4513] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                O que contém:
              </h3>
              <ul className="space-y-2" data-testid="product-items-list">
                {product.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-[#4A3728]">
                    <svg className="w-5 h-5 text-[#DAA520] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Note */}
            <div className="mt-8 p-4 bg-[#DED4C0] border-l-4 border-[#DAA520] rounded">
              <p className="text-sm text-[#4A3728]">
                <strong className="text-[#8B4513]">Observação:</strong> Os nossos produtos são caseiros e na falta de algum produto, o mesmo será 
                substituído por similar de igual valor e qualidade.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
